/* Copyright (c) 2020-present CloudIO Inc */

import {
  buttonSize,
  buttonVariant,
} from 'componentregistry/registry/constants';
import { Item } from '../../registry/Component.type';
import { page as SamplePage } from '../../help/sample_pages/SamplePageSimple';
import { Platform } from '../../platform/Platform';
import { baseComponentManager } from '../../registry/Components';
import { ComponentManagerFn } from '../../registry/Component.type';
import { ItemType } from '../../registry/ComponentRegistry';
import { defaultValue } from './ButtonGroup';
import { isValidButtonColor } from '../Button/Button';

const page: {
  form: Item<'Form'>;
  root: Item<'Page'>;
  flexItem: Item<'FlexItem'>;
  actionShowMessage: Item<'ActionShowMessage'>;
  rowPanel: Item<'RowPanel'>;
  flexPanel: Item<'FlexPanel'>;
  columnPanel: Item<'ColumnPanel'>;
  columnPanel1: Item<'ColumnPanel'>;
  columnPanel2: Item<'ColumnPanel'>;
  triggerOnClick: Item<'TriggerOnClick'>;
  dataStoreReference: Item<'DataStoreReference'>;
  localDataStore: Item<'LocalDataStore'>;
  actionCreate: Item<'ActionCreate'>;
  actionClear: Item<'ActionClear'>;
  triggerOnPageActive: Item<'TriggerOnPageActive'>;
  button: Item<'Button'>;
  button2: Item<'Button'>;
  button3: Item<'Button'>;
} = {
  ...SamplePage,
  button: {
    itemId: 'button',
    props: {
      text: { type: 'text', value: 'One' },
      startIcon: { iconName: 'check' },
    },
    children: ['triggerOnClick'],
    type: 'Button',
    parent: 'buttonGroup',
  },
  button2: {
    itemId: 'button2',
    props: {
      text: { type: 'text', value: 'Two' },
      startIcon: { iconName: '' },
    },
    children: [],
    type: 'Button',
    parent: 'buttonGroup',
  },
  button3: {
    itemId: 'button3',
    props: {
      text: { type: 'text', value: 'Three' },
      startIcon: { iconName: 'times' },
    },
    children: [],
    type: 'Button',
    parent: 'buttonGroup',
  },
};

const ButtonGroupManager: ComponentManagerFn<'ButtonGroup'> = (
  platform: Platform,
) => {
  return {
    ...baseComponentManager<'ButtonGroup'>(platform, 'ButtonGroup'),
    type: 'ButtonGroup',
    name: 'Button Group',
    isChildAllowed: (_page, item, childType) =>
      (['Button'] as ItemType[]).includes(childType),
    defaultValue,
    defaultChildren: ['Button', 'Button', 'Button'],
    props: {
      disableCondition: {
        label: 'Disable Condition',
        type: 'expressionOnly',
        returnType: () => 'boolean',
      },
      color: {
        label: 'Color',
        type: 'ThemeBGColor',
        filterColor: isValidButtonColor,
      },
      fullWidth: {
        label: 'Full Width',
        type: 'boolean',
        helpText:
          'If checked, the button will take up the full width of its container.',
      },
      size: {
        label: 'Size',
        type: 'select',
        options: buttonSize,
      },
      variant: {
        label: 'Variant',
        type: 'select',
        options: buttonVariant,
      },
    },
    getDisplayText: (item) => '',
    category: 'Toolbar',
    icon: { iconName: 'io-button-group', iconType: 'cloudio' },
    description: `**Overview**<br/>
The Button Group is a container component that organizes multiple related buttons into a unified visual block. It merges the borders of adjacent buttons, creating a cohesive "toolbar" or "toggle set" appearance.
Use this component when you have a set of related actions (like "Left," "Center," "Right" alignment) or mutually exclusive options that should be presented together.

**1. Structure & Inheritance**<br/>
The Button Group acts as a parent controller for its children.
* **Allowed Children:** It strictly accepts Button components.
* **Style Inheritance:** When buttons are placed inside a Button Group, they automatically inherit the Group's variant, color, and size settings. This ensures all buttons in the set look identical without needing to configure them individually.

**2. Configuration**<br/>
* **Variant:** Defines the shared style for all buttons (contained, outlined, or text).
* **Color:** Sets the shared theme color (e.g., primary, secondary).
* **Size:** Sets the shared size (small, medium, large).
* **Full Width:** If enabled, the entire group stretches to fill the container width.

**3. Advanced Behavior**<br/>
* **Disable Condition:** A single expression can disable the entire group of buttons at once, which is useful for disabling a whole set of controls based on a single state (e.g., "Read Only Mode").

**4. Best Use Cases**<br/>
* **View Toggles:** "List View" | "Grid View" | "Map View"
* **Editor Toolbars:** "Bold" | "Italic" | "Underline"
* **Rating Scales:** "Low" | "Medium" | "High"`,

    helpRenders: [
      {
        title: 'Default Button Group',
        description: 'Button Group rendered with Default Properties',
        page,
        exampleId: 'e1',
        clearParentChildren: true,
        hideProps: true,
        item: {
          itemId: 'buttonGroup',
          props: {},
          children: ['button', 'button2', 'button3'],
          type: 'ButtonGroup',
          parent: 'columnPanel1',
        },
      },
      {
        title: 'Button Group Variants',
        description: 'Button Group rendered with different variant',
        page,
        clearParentChildren: true,
        exampleId: 'e3',
        visibleProps: ['variant'],
        item: {
          itemId: 'buttonGroup',
          props: { variant: 'outlined' },
          children: ['button', 'button2', 'button3'],
          type: 'ButtonGroup',
          parent: 'columnPanel1',
        },
      },
      {
        title: 'Button Group Colors',
        description: 'Button Group rendered with different theme color',
        page,
        exampleId: 'e4',
        clearParentChildren: true,
        visibleProps: ['color'],
        item: {
          itemId: 'buttonGroup',
          props: { color: 'secondary' },
          children: ['button', 'button2', 'button3'],
          type: 'ButtonGroup',
          parent: 'columnPanel1',
        },
      },
    ],
  };
};

export default ButtonGroupManager;
